import { Button, Chip, Icon, Input, Modal, Spinner } from '@sjuni/ui';
import { FileText } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormError } from '@/components/FormError';
import { ApiRequestError } from '@/lib/api/client';

import { RunLog } from './RunLog';
import type { MigrationCommand, MigrationRun, MigrationStatus } from './types';
import { useMigrationHistory, useMigrationStatus, useRunCommand } from './useMigration';

/**
 * docs/05-ADMIN-DASHBOARD.md §5.9 — the migration console, Super Admin only.
 *
 * "connection test → dry-run with counts and a sample of what will be created →
 * run with a live progress log → a report of successes, skips, and failures with
 * reasons." Top to bottom, that is the order of this screen.
 *
 * ## Why the counts come in pairs
 *
 * Every number is shown beside the one it should match: 789 articles on the old
 * site, 729 records here. A console that only reported what it had done would
 * answer "did the command run" — which nobody is asking. The question is whether
 * the archive arrived, and that is a comparison.
 *
 * ## Why every step has two buttons
 *
 * A dry run does the real work — the real sanitiser, the real slug collisions —
 * inside a transaction it then rolls back, so its counts are the counts the real
 * run will produce. It is the only way to find out what a step will do to 789
 * articles without doing it, and putting it beside the real button is what makes
 * anybody use it.
 */
export function MigrationPage() {
  const { t } = useTranslation();
  const status = useMigrationStatus();
  const history = useMigrationHistory();
  const run = useRunCommand();

  const [openLog, setOpenLog] = useState<number | null>(null);
  const [options, setOptions] = useState<Record<string, Record<string, string>>>({});

  const data = status.data?.data;

  const start = (command: string, dryRun: boolean) => {
    run.mutate(
      {
        command,
        dry_run: dryRun,
        options: options[command] ?? {},
      },
      { onSuccess: (response) => setOpenLog(response.data.id) },
    );
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-h2 font-extrabold text-primary">{t('nav.items.migration')}</h1>
        <p className="mt-1 max-w-prose text-body-sm text-secondary">{t('migration.intro')}</p>
      </div>

      {status.isError ? <FormError message={t('errors.generic')} /> : null}
      <FormError message={runError(run.error, t)} />

      {status.isPending ? (
        <p className="flex items-center gap-2 text-body-sm text-secondary">
          <Spinner size="sm" label={t('common.loading')} />
          {t('common.loading')}
        </p>
      ) : null}

      {data ? (
        <>
          <Readiness data={data} />
          <Counts data={data} />

          <section className="space-y-3">
            <div>
              <h2 className="text-h4 font-bold text-primary">{t('migration.steps.title')}</h2>
              <p className="mt-1 max-w-prose text-body-sm text-secondary">
                {t('migration.steps.help')}
              </p>
            </div>

            <ol className="space-y-3">
              {data.commands.map((command, index) => (
                <li key={command.command}>
                  <Step
                    step={index + 1}
                    command={command}
                    latest={data.latest[command.command] ?? null}
                    uploadsPresent={data.uploads.present}
                    busy={run.isPending}
                    options={options[command.command] ?? {}}
                    onOption={(name, value) =>
                      setOptions((current) => ({
                        ...current,
                        [command.command]: { ...current[command.command], [name]: value },
                      }))
                    }
                    onRun={(dryRun) => start(command.command, dryRun)}
                    onOpenLog={setOpenLog}
                  />
                </li>
              ))}
            </ol>
          </section>

          <History runs={history.data?.data ?? []} onOpenLog={setOpenLog} />
        </>
      ) : null}

      {openLog === null ? null : (
        <Modal
          open
          onClose={() => setOpenLog(null)}
          title={t('migration.log.title')}
          closeLabel={t('common.close')}
          className="sm:w-[min(94vw,900px)]"
        >
          <RunLog id={openLog} />
        </Modal>
      )}
    </div>
  );
}

/** §5.9's connection test. */
function Readiness({ data }: { data: MigrationStatus }) {
  const { t } = useTranslation();

  const rows = [
    {
      label: t('migration.readiness.database'),
      ok: data.database.connected,
      okLabel: t('migration.readiness.connected'),
      failLabel: t('migration.readiness.disconnected'),
      detail: data.database.name,
      help: t('migration.readiness.databaseHelp'),
    },
    {
      label: t('migration.readiness.uploads'),
      ok: data.uploads.present,
      okLabel: t('migration.readiness.present'),
      failLabel: t('migration.readiness.missing'),
      detail: data.uploads.path,
      help: t('migration.readiness.uploadsHelp'),
    },
  ];

  return (
    <section className="space-y-3">
      <h2 className="text-h4 font-bold text-primary">{t('migration.readiness.title')}</h2>

      <div className="grid gap-3 sm:grid-cols-2">
        {rows.map((row) => (
          <div key={row.label} className="rounded-card border border-subtle bg-surface p-4">
            <div className="flex items-center justify-between gap-3">
              <span className="text-body-sm font-semibold text-primary">{row.label}</span>
              {/*
               * The state is a word as well as a colour: §7.4 forbids colour
               * alone, and "connected" is the thing being reported anyway.
               */}
              <Chip tone={row.ok ? 'teal' : 'rose'}>{row.ok ? row.okLabel : row.failLabel}</Chip>
            </div>
            <p className="mt-2 break-all font-mono text-caption text-muted">{row.detail}</p>
            <p className="mt-2 text-caption text-secondary">{row.help}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/** The two columns of numbers, side by side. */
function Counts({ data }: { data: MigrationStatus }) {
  const { t } = useTranslation();

  const columns = [
    { title: t('migration.counts.source'), values: data.source },
    { title: t('migration.counts.imported'), values: data.imported },
  ];

  return (
    <section className="grid gap-3 sm:grid-cols-2">
      {columns.map((column) => (
        <div key={column.title} className="rounded-card border border-subtle bg-surface p-4">
          <h2 className="text-body-sm font-semibold text-primary">{column.title}</h2>

          <dl className="mt-3 space-y-1.5">
            {Object.entries(column.values).map(([key, value]) => (
              <div key={key} className="flex items-baseline justify-between gap-3">
                <dt className="text-caption text-secondary">{t(`migration.counts.${key}`, key)}</dt>
                <dd className="font-mono text-body-sm font-semibold text-primary">
                  {value.toLocaleString()}
                </dd>
              </div>
            ))}
            {Object.keys(column.values).length === 0 ? (
              <p className="text-caption text-muted">{'—'}</p>
            ) : null}
          </dl>
        </div>
      ))}
    </section>
  );
}

type StepProps = {
  step: number;
  command: MigrationCommand;
  latest: MigrationRun | null;
  uploadsPresent: boolean;
  busy: boolean;
  options: Record<string, string>;
  onOption: (name: string, value: string) => void;
  onRun: (dryRun: boolean) => void;
  onOpenLog: (id: number) => void;
};

function Step({
  step,
  command,
  latest,
  uploadsPresent,
  busy,
  options,
  onOption,
  onRun,
  onOpenLog,
}: StepProps) {
  const { t } = useTranslation();

  const active = latest?.status === 'running' || latest?.status === 'queued';
  const blocked = command.needs_uploads && !uploadsPresent;

  return (
    <div className="rounded-card border border-subtle bg-surface p-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-navy text-caption font-bold text-inverse">
              {step}
            </span>
            <h3 className="text-body font-semibold text-primary">
              {t(`migration.commands.${stringKey(command.command)}.name`, command.command)}
            </h3>
            <code className="font-mono text-caption text-muted">{command.command}</code>
            {blocked ? <Chip tone="amber">{t('migration.steps.needsUploads')}</Chip> : null}
          </div>

          <p className="mt-2 max-w-prose text-body-sm text-secondary">
            {t(`migration.commands.${stringKey(command.command)}.description`, '')}
          </p>

          <RunSummary latest={latest} onOpenLog={onOpenLog} />
        </div>

        <div className="flex shrink-0 flex-col items-stretch gap-2">
          {command.dry_run ? (
            <Button
              variant="secondary"
              size="sm"
              disabled={busy || active || blocked}
              onClick={() => onRun(true)}
            >
              {t('migration.steps.dryRun')}
            </Button>
          ) : null}
          <Button
            variant="primary"
            size="sm"
            disabled={busy || active || blocked}
            onClick={() => onRun(false)}
            iconLeft={active ? <Spinner size="sm" label={t('migration.steps.running')} /> : null}
          >
            {active ? t('migration.steps.running') : t('migration.steps.run')}
          </Button>
        </div>
      </div>

      {command.options.length > 0 ? (
        <div className="mt-4 flex flex-wrap items-end gap-3 border-t border-subtle pt-4">
          {command.options.map((option) => (
            <Input
              key={option}
              label={t(`migration.steps.${option}`, option)}
              hint={t(`migration.steps.${option}Help`, '')}
              type={option === 'from' ? 'date' : 'number'}
              inputMode={option === 'from' ? undefined : 'numeric'}
              value={options[option] ?? ''}
              onChange={(event) => onOption(option, event.target.value)}
              className="w-44"
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** What happened last time, which is most of what this screen is for. */
function RunSummary({
  latest,
  onOpenLog,
}: {
  latest: MigrationRun | null;
  onOpenLog: (id: number) => void;
}) {
  const { t, i18n } = useTranslation();

  if (latest === null) {
    return <p className="mt-3 text-caption text-muted">{t('migration.steps.never')}</p>;
  }

  return (
    <p className="mt-3 flex flex-wrap items-center gap-2 text-caption text-secondary">
      <Chip tone={STATUS_TONES[latest.status]}>{t(`migration.status.${latest.status}`)}</Chip>

      {latest.dry_run ? <Chip tone="slate">{t('migration.history.dryRun')}</Chip> : null}

      <span>
        {t('migration.steps.lastRun')}
        {': '}
        {latest.created_at ? new Date(latest.created_at).toLocaleString(i18n.language) : '—'}
      </span>

      {latest.duration_seconds === null ? null : (
        <span>{t('migration.log.duration', { seconds: latest.duration_seconds })}</span>
      )}

      <button
        type="button"
        className="inline-flex items-center gap-1 font-semibold text-navy underline underline-offset-2"
        onClick={() => onOpenLog(latest.id)}
      >
        <Icon as={FileText} size="sm" />
        {t('migration.steps.viewLog')}
      </button>
    </p>
  );
}

function History({ runs, onOpenLog }: { runs: MigrationRun[]; onOpenLog: (id: number) => void }) {
  const { t, i18n } = useTranslation();

  return (
    <section className="space-y-3">
      <h2 className="text-h4 font-bold text-primary">{t('migration.history.title')}</h2>

      {runs.length === 0 ? (
        <p className="text-body-sm text-muted">{t('migration.history.empty')}</p>
      ) : (
        <div className="overflow-x-auto rounded-card border border-subtle bg-surface">
          <table className="w-full min-w-[40rem] text-left text-body-sm">
            <thead className="border-b border-subtle text-caption uppercase text-muted">
              <tr>
                <th scope="col" className="px-4 py-2 font-semibold">
                  {t('migration.history.when')}
                </th>
                <th scope="col" className="px-4 py-2 font-semibold">
                  {t('migration.history.command')}
                </th>
                <th scope="col" className="px-4 py-2 font-semibold">
                  {t('migration.history.who')}
                </th>
                <th scope="col" className="px-4 py-2 font-semibold">
                  {t('migration.history.result')}
                </th>
              </tr>
            </thead>
            <tbody>
              {runs.map((entry) => (
                <tr key={entry.id} className="border-b border-subtle last:border-0">
                  <td className="px-4 py-2 text-secondary">
                    {entry.created_at
                      ? new Date(entry.created_at).toLocaleString(i18n.language)
                      : '—'}
                  </td>
                  <td className="px-4 py-2">
                    <code className="font-mono text-caption">{entry.command}</code>
                    {entry.dry_run ? (
                      <span className="ml-2 text-caption text-muted">
                        {t('migration.history.dryRun')}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 text-secondary">{entry.author?.name ?? '—'}</td>
                  <td className="px-4 py-2">
                    <button
                      type="button"
                      className="inline-flex items-center gap-2"
                      onClick={() => onOpenLog(entry.id)}
                    >
                      <Chip tone={STATUS_TONES[entry.status]}>
                        {t(`migration.status.${entry.status}`)}
                      </Chip>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * A command name as an i18n key.
 *
 * i18next reads `:` as its namespace separator, so `migration.commands.wp:analyze`
 * is looked up as key `migration.commands.wp` in a namespace called `analyze` —
 * which does not exist, so every command on the screen showed its own key instead
 * of its name. The locale files hold `wp-analyze`.
 */
function stringKey(command: string): string {
  return command.replace(/:/g, '-');
}

const STATUS_TONES = {
  queued: 'slate',
  running: 'sky',
  succeeded: 'teal',
  failed: 'rose',
} as const;

/**
 * §6.3 — "map 422 responses onto the offending fields. Never show a raw JSON
 * error." There is one field here worth mapping, and the server's message on it
 * says what to do about it.
 */
function runError(error: unknown, t: (key: string) => string): string | null {
  if (error === null || error === undefined) {
    return null;
  }

  if (error instanceof ApiRequestError) {
    return error.validationErrors.command?.[0] ?? error.message;
  }

  return t('errors.generic');
}
